import { PlatformError } from '@djgg/core';
import { CONCEPT_MAX_LEN } from './inputLimits.js';
import type { StripAssetMeta } from './types.js';

export const CREATION_FILM_SCHEMA_VERSION = 1 as const;
export const CREATION_FILM_MATERIAL_LIMIT = 96;
/** The workshop Deej cards a film may show, with the title a creator reads. */
export const CREATION_FILM_DEEJ_TITLES = {
  'deej-film-spark-catcher': 'Spark Catcher',
  'deej-film-rule-cartographer': 'Rule Cartographer',
  'deej-film-raw-art-receiver': 'Raw-Art Receiver',
  'deej-film-cutout-technician': 'Cutout Technician',
  'deej-film-casting-director': 'Casting Director',
  'deej-film-beat-builder': 'Beat Builder',
  'deej-film-crash-test-pilot': 'Crash-Test Pilot',
  'deej-film-fix-mechanic': 'Fix Mechanic',
  'deej-film-trailer-director': 'Trailer Director',
} as const;
export type CreationFilmDeejAssetId = keyof typeof CREATION_FILM_DEEJ_TITLES;
export const CREATION_FILM_DEEJ_ASSET_IDS = Object.keys(CREATION_FILM_DEEJ_TITLES) as CreationFilmDeejAssetId[];

export const CREATION_FILM_PROTECTED_MATERIAL_KINDS = [
  'title',
  'cover',
  'soundtrack',
  'gameplay-capture',
  'playable-proof',
] as const;

export type CreationFilmProtectedMaterialKind =
  (typeof CREATION_FILM_PROTECTED_MATERIAL_KINDS)[number];

export type CreationFilmMaterialKind =
  | CreationFilmProtectedMaterialKind
  | 'concept'
  | 'cue'
  | 'raw-preview'
  | 'processed-art'
  | 'sprite-strip'
  | 'deej'
  | 'other';

export type CreationFilmRunBindingV1 =
  | { kind: 'analysis'; operationId: string; revision: number }
  | { kind: 'version'; version: number }
  | { kind: 'revision'; revisionRunId: string; baseVersion: number; version: number | null };

/** Pure, cross-runtime identity for one creation/binding authority. */
export function creationFilmRunIdentityV1(
  creationId: string,
  binding: CreationFilmRunBindingV1,
): { runId: string; seed: number } {
  assertId(creationId, 'creationId');
  const authority = binding.kind === 'analysis'
    ? [binding.kind, binding.operationId, binding.revision]
    : binding.kind === 'version'
      ? [binding.kind, binding.version]
      : [binding.kind, binding.revisionRunId, binding.baseVersion, binding.version];
  const canonical = JSON.stringify([CREATION_FILM_SCHEMA_VERSION, creationId, authority]);
  let hash = 0xcbf29ce484222325n;
  for (const byte of new TextEncoder().encode(canonical)) {
    hash ^= BigInt(byte);
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return {
    runId: `film-${hash.toString(16).padStart(16, '0')}`,
    seed: Number(hash & 0xffff_ffffn),
  };
}

export interface CreationFilmRunV1 {
  schemaVersion: 1;
  runId: string;
  creationId: string;
  /** Stable non-negative integer used to replay deterministic editorial choices. */
  seed: number;
  binding: CreationFilmRunBindingV1;
  status: 'collecting' | 'ready' | 'failed';
  materialCount: number;
  createdAt: number;
  updatedAt: number;
  failure?: { code: string; message: string; failedAt: number } | null;
}

/** What a viewer needs to draw one frame of a strip; generation lineage stays on the asset. */
export type CreationFilmStripV1 = Omit<StripAssetMeta, 'referenceVariantId'>;

export interface CreationFilmMaterialV1 {
  schemaVersion: 1;
  runId: string;
  materialId: string;
  creationId: string;
  kind: CreationFilmMaterialKind;
  source: 'analysis' | 'version' | 'revision' | 'reference' | 'generated' | 'capture' | 'raw';
  title: string;
  uri?: string;
  text?: string;
  deejAssetId?: string;
  contentType?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  /** Frame layout, required to draw a sprite strip as one frame instead of the whole sheet. */
  strip?: CreationFilmStripV1;
  description?: string;
  unavailableReason?: string;
  createdAt: number;
}

export interface CreationFilmSceneV1 {
  schemaVersion: 1;
  sceneId: string;
  kind: 'intro' | 'world' | 'character' | 'gameplay' | 'proof' | 'outro';
  title: string;
  durationMs: number;
  materialIds: string[];
  narration?: string;
  caption?: string;
}

export interface CreationFilmManifestV1 {
  schemaVersion: 1;
  runId: string;
  creationId: string;
  version: number;
  /** Highlight is a sealed 45–90s edit; fallback is an explicitly minimal honest edit. */
  mode: 'highlight' | 'fallback';
  title: string;
  materialIds: string[];
  scenes: CreationFilmSceneV1[];
  createdAt: number;
}

const ALL_MATERIAL_KINDS = new Set<CreationFilmMaterialKind>([
  ...CREATION_FILM_PROTECTED_MATERIAL_KINDS,
  'concept', 'cue', 'raw-preview', 'processed-art', 'sprite-strip', 'deej', 'other',
]);
const MATERIAL_SOURCES = new Set<CreationFilmMaterialV1['source']>([
  'analysis', 'version', 'revision', 'reference', 'generated', 'capture', 'raw',
]);
const SCENE_KINDS = new Set<CreationFilmSceneV1['kind']>([
  'intro', 'world', 'character', 'gameplay', 'proof', 'outro',
]);

function fail(message: string): never {
  throw new PlatformError('validation', `invalid creation film: ${message}`);
}

function assertId(value: unknown, field: string): asserts value is string {
  if (typeof value !== 'string' || value.length < 1 || value.length > 200 || value.includes('/')) {
    fail(`${field} must be a 1..200 character path-safe id`);
  }
}

function assertText(value: unknown, field: string, max: number): asserts value is string {
  if (typeof value !== 'string' || value.trim().length < 1 || value.length > max) {
    fail(`${field} must be non-empty and at most ${max} characters`);
  }
}

function assertTime(value: unknown, field: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) fail(`${field} must be a timestamp`);
}

function assertPositiveInteger(value: unknown, field: string): asserts value is number {
  if (!Number.isInteger(value) || (value as number) < 1) fail(`${field} must be a positive integer`);
}

export function isProtectedCreationFilmMaterialKind(
  kind: CreationFilmMaterialKind,
): kind is CreationFilmProtectedMaterialKind {
  return (CREATION_FILM_PROTECTED_MATERIAL_KINDS as readonly string[]).includes(kind);
}

export function validateCreationFilmRunV1(run: CreationFilmRunV1): CreationFilmRunV1 {
  if (run.schemaVersion !== CREATION_FILM_SCHEMA_VERSION) fail('unsupported run schemaVersion');
  assertId(run.runId, 'runId');
  assertId(run.creationId, 'creationId');
  if (!Number.isSafeInteger(run.seed) || run.seed < 0) fail('seed must be a non-negative safe integer');
  if (!run.binding || typeof run.binding !== 'object') fail('binding is required');
  if (run.binding.kind === 'analysis') {
    assertId(run.binding.operationId, 'binding.operationId');
    assertPositiveInteger(run.binding.revision, 'binding.revision');
  } else if (run.binding.kind === 'version') {
    assertPositiveInteger(run.binding.version, 'binding.version');
  } else if (run.binding.kind === 'revision') {
    assertId(run.binding.revisionRunId, 'binding.revisionRunId');
    assertPositiveInteger(run.binding.baseVersion, 'binding.baseVersion');
    if (run.binding.version !== null) assertPositiveInteger(run.binding.version, 'binding.version');
  } else {
    fail('binding.kind is unsupported');
  }
  if (!['collecting', 'ready', 'failed'].includes(run.status)) fail('status is unsupported');
  if (run.status === 'failed' && !run.failure) fail('failed run requires failure details');
  if (run.status !== 'failed' && run.failure) fail('non-failed run cannot retain failure details');
  if (!Number.isInteger(run.materialCount) || run.materialCount < 0 || run.materialCount > CREATION_FILM_MATERIAL_LIMIT) {
    fail(`materialCount must be between 0 and ${CREATION_FILM_MATERIAL_LIMIT}`);
  }
  assertTime(run.createdAt, 'createdAt');
  assertTime(run.updatedAt, 'updatedAt');
  if (run.failure) {
    assertText(run.failure.code, 'failure.code', 100);
    assertText(run.failure.message, 'failure.message', 500);
    assertTime(run.failure.failedAt, 'failure.failedAt');
  }
  return run;
}

export function validateCreationFilmMaterialV1(
  material: CreationFilmMaterialV1,
): CreationFilmMaterialV1 {
  if (material.schemaVersion !== CREATION_FILM_SCHEMA_VERSION) fail('unsupported material schemaVersion');
  assertId(material.runId, 'runId');
  assertId(material.materialId, 'materialId');
  assertId(material.creationId, 'creationId');
  if (!ALL_MATERIAL_KINDS.has(material.kind)) fail('material kind is unsupported');
  if (!MATERIAL_SOURCES.has(material.source)) fail('material source is unsupported');
  assertText(material.title, 'material title', 200);
  if (!material.uri && !material.text && !material.deejAssetId && !material.unavailableReason) {
    fail('material requires uri, text, deejAssetId, or unavailableReason');
  }
  if (material.uri !== undefined) assertText(material.uri, 'material uri', 2_000);
  if (material.text !== undefined) assertText(material.text, 'material text', CONCEPT_MAX_LEN);
  if (material.deejAssetId !== undefined) assertId(material.deejAssetId, 'material deejAssetId');
  if (material.contentType !== undefined) assertText(material.contentType, 'material contentType', 200);
  if (material.description !== undefined) assertText(material.description, 'material description', 1_000);
  if (material.unavailableReason !== undefined) assertText(material.unavailableReason, 'material unavailableReason', 500);
  for (const [field, value] of [['width', material.width], ['height', material.height], ['durationMs', material.durationMs]] as const) {
    if (value !== undefined && (typeof value !== 'number' || !Number.isFinite(value) || value <= 0)) {
      fail(`material ${field} must be positive`);
    }
  }
  if (material.kind === 'sprite-strip' && material.uri && !material.strip) fail('sprite-strip material requires its frame layout');
  if (material.strip !== undefined) {
    assertText(material.strip.entityName, 'material strip entityName', 200);
    assertText(material.strip.state, 'material strip state', 200);
    for (const value of [material.strip.frames, material.strip.frameWidth, material.strip.frameHeight]) {
      if (!Number.isInteger(value) || value < 1) fail('material strip geometry must be positive integers');
    }
  }
  assertTime(material.createdAt, 'createdAt');
  return material;
}

export function validateCreationFilmManifestV1(
  manifest: CreationFilmManifestV1,
): CreationFilmManifestV1 {
  if (manifest.schemaVersion !== CREATION_FILM_SCHEMA_VERSION) fail('unsupported manifest schemaVersion');
  assertId(manifest.runId, 'runId');
  assertId(manifest.creationId, 'creationId');
  assertPositiveInteger(manifest.version, 'version');
  if (manifest.mode !== 'highlight' && manifest.mode !== 'fallback') fail('manifest mode is unsupported');
  assertText(manifest.title, 'manifest title', 200);
  assertTime(manifest.createdAt, 'createdAt');
  if (!Array.isArray(manifest.materialIds) || manifest.materialIds.length > 48) {
    fail('manifest materialIds exceeds 48');
  }
  manifest.materialIds.forEach((id, index) => assertId(id, `materialIds[${index}]`));
  if (new Set(manifest.materialIds).size !== manifest.materialIds.length) fail('manifest materialIds must be unique');
  if (!Array.isArray(manifest.scenes) || manifest.scenes.length < 1 || manifest.scenes.length > 24) {
    fail('manifest scenes must contain 1..24 entries');
  }
  const available = new Set(manifest.materialIds);
  const sceneIds = new Set<string>();
  let totalDurationMs = 0;
  for (const scene of manifest.scenes) {
    if (scene.schemaVersion !== CREATION_FILM_SCHEMA_VERSION) fail('unsupported scene schemaVersion');
    assertId(scene.sceneId, 'sceneId');
    if (sceneIds.has(scene.sceneId)) fail('scene ids must be unique');
    sceneIds.add(scene.sceneId);
    if (!SCENE_KINDS.has(scene.kind)) fail('scene kind is unsupported');
    assertText(scene.title, 'scene title', 200);
    if (!Number.isInteger(scene.durationMs) || scene.durationMs < 250 || scene.durationMs > 120_000) {
      fail('scene durationMs must be an integer between 250 and 120000');
    }
    totalDurationMs += scene.durationMs;
    if (!Array.isArray(scene.materialIds) || scene.materialIds.length > CREATION_FILM_MATERIAL_LIMIT) {
      fail('scene materialIds is invalid');
    }
    for (const id of scene.materialIds) {
      assertId(id, 'scene material id');
      if (!available.has(id)) fail(`scene references undeclared material ${id}`);
    }
    if (new Set(scene.materialIds).size !== scene.materialIds.length) fail('scene materialIds must be unique');
    if (scene.narration !== undefined) assertText(scene.narration, 'scene narration', 2_000);
    if (scene.caption !== undefined) assertText(scene.caption, 'scene caption', 500);
  }
  if (manifest.mode === 'highlight' && (totalDurationMs < 45_000 || totalDurationMs > 90_000)) {
    fail('highlight duration must be between 45000 and 90000ms');
  }
  if (manifest.mode === 'fallback' && (totalDurationMs < 250 || totalDurationMs > 45_000)) {
    fail('fallback duration must be between 250 and 45000ms');
  }
  return manifest;
}

/** Deterministically retain protected evidence first, then newest optional material. */
export function compactCreationFilmMaterials(
  materials: readonly CreationFilmMaterialV1[],
): CreationFilmMaterialV1[] {
  const unique = new Map<string, CreationFilmMaterialV1>();
  for (const material of materials) {
    validateCreationFilmMaterialV1(material);
    if (unique.has(material.materialId)) fail(`duplicate material id ${material.materialId}`);
    unique.set(material.materialId, material);
  }
  const order = (left: CreationFilmMaterialV1, right: CreationFilmMaterialV1): number =>
    right.createdAt - left.createdAt || left.materialId.localeCompare(right.materialId);
  const protectedMaterials = [...unique.values()].filter(material => isProtectedCreationFilmMaterialKind(material.kind)).sort(order);
  if (protectedMaterials.length > CREATION_FILM_MATERIAL_LIMIT) {
    throw new PlatformError('quota', `protected creation film material exceeds ${CREATION_FILM_MATERIAL_LIMIT}`, { retryable: false });
  }
  const optional = [...unique.values()].filter(material => !isProtectedCreationFilmMaterialKind(material.kind)).sort(order);
  return [...protectedMaterials, ...optional.slice(0, CREATION_FILM_MATERIAL_LIMIT - protectedMaterials.length)];
}
